<template>
    <div class="tpi-block">
        <div class="flex flex-wrap border-b border-gray-400 mt-4 pb-1 mb-2 md:mb-0">
            <div class="w-full sm:w-3/4 mt-2">
                <!-- <select-c v-model="market" :options="markets" label="Market: " :disabled="isGetData && period=='custom'"></select-c> -->
                <label class="leading-none self-center text-black mr-2" for="tpiMarket">Market:</label>
                <select class="form-control form-control-sm form-control-fixed bg-white mb-1" v-model="market" id="tpiMarket" aria-label="Select Market" :disabled="isGetData && period == 'custom'">
                    <template v-for="option in markets">
                        <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                            {{ option.label }}
                        </option>
                    </template>
                </select>
            </div>
            <dt-flip-charts-button class="ml-auto xs:mt-3 print:hidden"></dt-flip-charts-button>
        </div>

        <dt-commodity-navigation v-if="market === 'futures'" class="print:hidden"></dt-commodity-navigation>

        <div class="border-t-2 border-blue-600 mt-2">
        <div class="table-header flex flex-wrap ">
            <div class="w-full sm:w-3/4 mt-1">
                <div class="inline-block">
                    <!-- <select-c v-model="period" :options="periods" label="Period: " :disabled="isGetData && period=='custom'"></select-c> -->
                    <label class="leading-none self-center text-black mr-2" for="tpiPeriod">Period:</label>
                    <select class="form-control form-control-sm form-control-fixed bg-white mb-1" v-model="period" id="tpiPeriod" aria-label="Select Market" :disabled="isGetData && period == 'custom'">
                        <template v-for="option in periods">
                            <option :class="{ 'bg-yellow-200': option.highlight }" :value="option.value">
                                {{ option.label }}
                            </option>
                        </template>
                    </select>
                </div>
                <div class="inline-block" v-if="isJim() && period == 'custom'">
                    <div class="form-group-addon form-group-addon-sm ml-4 -mt-1">
                        <span class="input-addon-item bg-gray-100 font-bold">Enter period in minute(s):</span>
                        <input id="minutes"
                            @blur="changeMinutes"
                            placeholder="mins."
                            type="number"
                            min="1"
                            v-model.number="inputMinutes"
                            class="form-control-sm form-input-inline-w-none form-input-sm-inline input-addon-field w-16"
                            :disabled="isGetData"
                        />
                        <button class="input-addon-item btn-yellow text-white font-bold" @click="changeMinutes" :disabled="isGetData">Get Data</button>
                        <spinner v-if="isGetData" class="get-tpi ml-2"></spinner>
                    </div>
                </div>
            </div>
            <dt-show-triggered-markets class="ml-auto mt-1 xs:mt-3" v-if="period!='custom'"></dt-show-triggered-markets>
        </div>
        </div>

        <data-table v-if="loadTable" :default-request="request" :default-columns="columns"></data-table>

        <flash-message secondsToDisplay="3" ref="flash"></flash-message>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, inject } from 'vue';
import { useLocationHash } from '../../mixins/LocationHash';
import { useIsJim } from '../../mixins/IsJim';

const props = defineProps({
    type: {
        type: String,
        required: true
    },
    defaultColumns: {
        type: Object,
        required: true
    }
});

const eventBus = inject('eventBus');

const { addToHash, searchHash } = useLocationHash();
const { isJim } = useIsJim();

const flash = ref(null);

const markets = ref([
    { label: 'Futures', value: 'futures' },
    { label: 'Forex', value: 'forex' }
]);
const periods = ref(null);

const market = ref(searchHash('market') || 'futures');
const period =  ref(searchHash('period') || 'intraday');
const inputMinutes = ref(searchHash('minutes') || 60);
const minutes = ref(searchHash('minutes') || 60);
const isGetData = ref(false);
const loadTable = ref(true);

const request = computed(() => ({
    market: market.value,
    period: period.value,
    minutes: minutes.value,
    type: props.type,
    groupBy: groupBy.value,
    route: window.route('api.tpi-cheat-sheet')
}));

const groupBy = computed(() => {
    if (market.value === 'futures') {
        return ['uscCategory', 'symbolName'];
    }
});

const columns = computed(() => {
    if (market.value === 'futures') {
        if (period.value === 'custom') {
            return props.defaultColumns.futures.custom;
        } else if (period.value === 'eod') {
            return props.defaultColumns.futures.eod;
        } else {
            return props.defaultColumns.futures.intraday;
        }
    } else if (market.value === 'forex') {
        if (period.value === 'custom') {
            return props.defaultColumns.forex.custom;
        } else if (period.value === 'eod') {
            return props.defaultColumns.forex.eod;
        } else {
            return props.defaultColumns.forex.intraday;
        }
    }
});

const validateMinutes = () => {
    if (!inputMinutes.value || inputMinutes.value.length === 0 || isNaN(parseInt(inputMinutes.value))) {
        inputMinutes.value = null;
        return false;
    }
    return true;
};

const changeMinutes = () => {
    if (!validateMinutes()) {
        return;
    }

    if (minutes.value != inputMinutes.value) {
        minutes.value = inputMinutes.value;
        isGetData.value = true;
    } else {
        loadDataOneByOne();
    }
};

const loadDataOneByOne = async () => {
    isGetData.value = true;

    const route = window.route('api.tpi-cheat-sheet-symbol');

    let table = document.querySelector('.tpi-block .data-table');
    let rows = table.querySelectorAll('tbody td[data-type=TPI]');
    let minutes = minutes.value;

    for (let i = 0; i < rows.length; i++) {
        rows[i].innerHTML = '⟳';
        let symbol = rows[i].parentElement.querySelector('td[data-type=Symbol] div').innerText;
        let trendSpotterSignalClass = rows[i].parentElement.querySelector('td[data-type=TrendSeeker] div').classList;
        const params = {
            symbol: symbol,
            minutes: minutes,
            trendSpotterSignal: trendSpotterSignalClass.value
        };

        // try {
        //     const response = await axios.post(route, params, { timeout: 30000 });
        //     rows[i].innerHTML = response.data.tpi;
        //     rows[i].parentElement.querySelector('td[data-type=TPITime] div').innerHTML = response.data.tpiDate;
        //     if (response.data.highlight) {
        //         rows[i].parentElement.classList.add('highlight');
        //     }
        // } catch (error) {
        //     flash.value.error(`Error getting the TPI data for symbol: ${symbol}`);
        //     rows[i].innerHTML = 'Error';
        // }

        await axios.post(route, params, { timeout: 30000 }).then((response) => {
            rows[i].innerHTML = response.data.tpi;
            rows[i].parentElement.querySelector('td[data-type=TPITime] div').innerHTML = response.data.tpiDate;
            if (response.data.highlight) {
                rows[i].parentElement.classList.add('highlight');
            }
        }).catch((error) => {
            flash.value.error(`Error getting the TPI data for symbol: ${symbol}`);
            rows[i].innerHTML = 'Error';
        });
    }

    isGetData.value = false;
};

watch(() => market.value, newValue => addToHash('market', newValue));
watch(() => period.value, newValue => addToHash('period', newValue));
watch(() => minutes.value, newValue => addToHash('minutes', newValue));

onMounted(() => {

    const defaultPeriod = searchHash('period');
    periods.value = [
        { label: 'Intraday', value: 'intraday', default: (defaultPeriod && defaultPeriod.value === 'intraday') },
        { label: 'End-of-Day', value: 'eod', default: (defaultPeriod && defaultPeriod.value === 'eod') },
    ];
    if (isJim()) {
        periods.value.push({ label: 'Custom', value: 'custom', default: (defaultPeriod && defaultPeriod.value === 'custom') });
    }

    loadTable.value = true;
    eventBus.on('data-loaded', () => {
        if (period.value == 'custom') {
            setTimeout(() => {
                loadDataOneByOne();
            }, 1000);
        }
    });
});
</script>

<style lang="scss">
.tpi-block {
    .spinner.get-tpi {
        width: 24px;
        height: 24px;
        margin-top: auto;
        margin-bottom: auto;

        .spinner-child {
            background-color: #d38653 !important;
        }
    }

    .form-group-addon.form-group-addon-sm {
        margin-bottom: 0 !important;
        .input-addon-field,
        .input-addon-item {
            box-shadow: none !important;
            font-size: 0.815rem !important;
        }
    }
}
</style>
